/* eslint-disable radix */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import getSelectedElements from './getSelectedElements';
import FontSelect from '../../../../componentLibrary/FontSelect';
import Button from '../../../../componentLibrary/Button';
import { getLineHeight, getLineHeightMultiplier } from '../../../CenterPanel/LineHeight';
import * as columnItemActions from '../../../../actions/columnItem';
import { blockType } from '../../../../types';

class FontSettings extends PureComponent {
    state = {
        selectedElements: undefined,
        content: null,
        elements: [],
        contentValues: {
            fontFamily: 'Arial',
            fontID: null,
            fontSize: '16px',
            lineHeight: '1px',
            align: 'center',
        },
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { canvasBlocks, selectedElements } = nextProps;
        const elmIdx = Object.keys(selectedElements.local)[0];
        const { blockIndex, columnIdx } = selectedElements.local[elmIdx];
        const firstElementData = canvasBlocks
            .find((block) => block.index === blockIndex)
            .content.columns
            .find((column) => column.columnIdx === columnIdx)
            .columnItems
            .find((elm) => elm.elmIdx === elmIdx);


        const { content } = firstElementData;
        const { fontFamily, fontID, fontSize, lineHeight, align } = content;

        let newElements = prevState.elements;
        if (prevState.selectedElements !== selectedElements) {
            newElements = getSelectedElements(selectedElements.local);
        }

        const contentValues = { fontFamily, fontID, fontSize, align, lineHeight };
        return { ...prevState, contentValues, elements: newElements };
    }

    _updateContentInAlBlocks(key, value) {
        const { elements, contentValues } = this.state;
        const { dispatch } = this.props;
        let property = { key, value };

        let newContentValues;
        if (key === 'fontSize') {
            const { fontSize, lineHeight } = contentValues;
            const lineHeightMultiplier = getLineHeightMultiplier(fontSize, lineHeight);
            const lineHeightValue = getLineHeight(value, lineHeightMultiplier);

            // we need to make sure the line height is updated using the multiplier so we keep the same proportion
            dispatch(columnItemActions.updateColumnItemContentInMultipleBlocks({ elements, property: { key: 'lineHeight', value: lineHeightValue } }));

            newContentValues = { ...contentValues, [key]: value, lineHeight: lineHeightValue };
        } else if (key === 'lineHeight') {
            const { fontSize } = contentValues;

            // when the lineHeight is changed, we need to change the fontSize for all the elements, otherwise the fontHeight will be inconsistent
            // among the selected elements and will have values that are not under the scale of valid values
            dispatch(columnItemActions.updateColumnItemContentInMultipleBlocks({ elements, property: { key: 'fontSize', value: fontSize } }));

            newContentValues = { ...contentValues, [key]: value };
        } else if (key === 'fontFamily') {
            const { fontID, value: fontValue } = value;
            newContentValues = { ...contentValues, [key]: fontValue.trim(), fontID };

            // setting the value here since now we are passing object containing value and fontID
            property = { key, value: fontValue.trim() };

            // need to dispatch the update for fontID for all the elements as well
            dispatch(columnItemActions.updateColumnItemContentInMultipleBlocks({ elements, property: { key: 'fontID', value: fontID } }));
        } else {
            newContentValues = { ...contentValues, [key]: value };
        }

        dispatch(columnItemActions.updateColumnItemContentInMultipleBlocks({ elements, property }));
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
        }));
    }

    render() {
        const { contentValues } = this.state;
        const { fontFamily, fontID, fontSize, align, lineHeight } = contentValues;
        const currLineHeight = String(parseFloat(lineHeight.split('px')[0] / fontSize.split('px')[0]).toFixed(1)).replace(/.0/g, '');

        return (
            <React.Fragment>
                <div style={{ width: '100%', height: 1, margin: '0 auto', background: '#D9DADB', marginBottom: 15 }} />
                <FontSelect
                    width={180}
                    type="fontFamily"
                    label="Font Family"
                    value={fontFamily}
                    fontID={fontID}
                    onChange={(selectedOption) => {
                        const selectedFontFamilyValue = selectedOption.value.trim();
                        if (selectedFontFamilyValue === fontFamily) {
                            return;
                        }

                        this._updateContentInAlBlocks('fontFamily', selectedOption);
                    }}
                />
                <FontSelect
                    width={82}
                    type="fontSize"
                    label="Font Size"
                    value={fontSize}
                    icon="format_size"
                    marginBottom={3}
                    onChange={(selectedOption) => {
                        const selectedFontSize = selectedOption.value.trim();
                        if (selectedFontSize === fontSize) {
                            return;
                        }

                        this._updateContentInAlBlocks('fontSize', selectedFontSize);
                    }}
                />
                <FontSelect
                    width={82}
                    type="lineHeight"
                    label="Line Height"
                    value={currLineHeight}
                    icon="format_line_spacing"
                    marginBottom={3}
                    onChange={(selectedOption) => {
                        const selectedLineHeight = selectedOption.value.trim();
                        if (selectedLineHeight === currLineHeight) {
                            return;
                        }
                        const newLineHeight = `${parseInt(parseFloat(selectedLineHeight) * parseInt(fontSize.split('px')[0]))}px`;

                        this._updateContentInAlBlocks('lineHeight', newLineHeight);
                    }}
                />
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        type="simple"
                        icon="align-left"
                        width={28}
                        iconSize={14}
                        marginBottom={1}
                        color="#727272"
                        label="Align Left"
                        onClick={() => {
                            if (align === 'left') {
                                return;
                            }
                            this._updateContentInAlBlocks('align', 'left');
                        }}
                    />
                    <Button
                        type="simple"
                        icon="align-center"
                        width={28}
                        iconSize={14}
                        marginBottom={1}
                        color="#727272"
                        label="Align Center"
                        onClick={() => {
                            if (align === 'center') {
                                return;
                            }
                            this._updateContentInAlBlocks('align', 'center');
                        }}
                    />
                    <Button
                        type="simple"
                        icon="align-right"
                        width={28}
                        iconSize={14}
                        marginBottom={1}
                        color="#727272"
                        label="Align Right"
                        onClick={() => {
                            if (align === 'right') {
                                return;
                            }
                            this._updateContentInAlBlocks('align', 'right');
                        }}
                    />
                </div>
            </React.Fragment>
        );
    }
}

FontSettings.propTypes = {
    dispatch: PropTypes.func.isRequired,
    canvasBlocks: PropTypes.arrayOf(blockType).isRequired,
    selectedElements: PropTypes.shape({
        local: PropTypes.object,
        remote: PropTypes.object,
    }).isRequired,
};

export default connect()(FontSettings);
